// Text-to-Speech utility for accessibility
import { logger } from './logger';
import { storage, STORAGE_KEYS } from './storage';

class TextToSpeech {
  constructor() {
    this.synth = window.speechSynthesis;
    this.isSupported = 'speechSynthesis' in window;
    this.isPlaying = false;
    this.currentUtterance = null;
    this.selectedVoice = null;
    
    // Load user preferences from storage
    this.userPreferences = this.loadUserPreferences();
    
    // Custom pronunciation dictionary for educational terms
    this.pronunciationDictionary = {
      // Math terms
      'add': 'add',
      'subtract': 'subtract',
      'multiply': 'multiply',
      'divide': 'divide',
      'equals': 'equals',
      'plus': 'plus',
      'minus': 'minus',
      'times': 'times',
      // Phonics sounds (will be handled by enhanceTextForSpeech)
    };
    
    // Prioritize neural and premium voices that sound more natural
    this.preferredVoices = [
      // Google Neural voices (most natural)
      'Google US English (Neural)',
      'Google en-US Neural',
      'Google US English',
      'Google en-US',
      'Google US English (Enhanced)',
      // Apple voices (natural sounding)
      'Samantha Enhanced',
      'Samantha Premium',
      'Samantha',
      'Alex Enhanced',
      'Alex Premium',
      'Alex',
      'Victoria Enhanced',
      'Victoria',
      'Karen Enhanced',
      'Karen',
      'Moira Enhanced',
      'Moira',
      // Microsoft Neural voices
      'Microsoft Aria Neural',
      'Microsoft Aria Online (Natural)',
      'Microsoft Aria',
      'Microsoft Zira Neural',
      'Microsoft Zira',
      'Microsoft Guy Neural',
      'Microsoft Guy',
      'Microsoft David Neural',
      'Microsoft David',
      // Other quality voices
      'Veena',
      'Tessa',
      'Fiona',
      'Daniel'
    ];
    this.voiceLoaded = false;
    this.audioUnlocked = false;
    this.audioCtx = null;
    
    // Load voices when available
    this.loadVoices();

    // Attempt to unlock audio on first user gesture (iOS/Safari/WebAudio quirk)
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const unlock = () => {
        try {
          if (this.synth && this.synth.resume) this.synth.resume();
          this.unlockAudioContext();
          this.audioUnlocked = true;
        } catch (_) {}
        document.removeEventListener('touchstart', unlock, { passive: true });
        document.removeEventListener('click', unlock, true);
        document.removeEventListener('keydown', unlock, true);
      };
      document.addEventListener('touchstart', unlock, { passive: true });
      document.addEventListener('click', unlock, true);
      document.addEventListener('keydown', unlock, true);
    }
  }

  // Load available voices
  loadVoices() {
    if (!this.isSupported) return;
    
    const loadVoicesList = () => {
      const voices = this.synth.getVoices();
      if (voices.length > 0) {
        this.voiceLoaded = true;
        this.selectBestVoice(voices);
      }
    };

    // Try to load voices immediately
    loadVoicesList();
    
    // Also listen for voice changes (some browsers load voices asynchronously)
    this.synth.onvoiceschanged = loadVoicesList;
  }

  // Select the best available voice
  selectBestVoice(voices) {
    // Check if user has a saved voice preference
    if (this.userPreferences?.voiceName) {
      const savedVoice = voices.find(v => 
        v.name === this.userPreferences.voiceName ||
        v.name.toLowerCase().includes(this.userPreferences.voiceName.toLowerCase())
      );
      if (savedVoice && savedVoice.lang.startsWith('en')) {
        this.selectedVoice = savedVoice;
        logger.debug(`Selected user preferred voice: ${savedVoice.name}`);
        return;
      }
    }
    
    // First try to find a preferred voice (prioritize neural/premium voices)
    for (const preferredName of this.preferredVoices) {
      const voice = voices.find(v => {
        const name = v.name.toLowerCase();
        const preferred = preferredName.toLowerCase();
        return name.includes(preferred) || 
               name.includes(preferred.replace(' enhanced', '')) ||
               name.includes(preferred.replace(' neural', '')) ||
               name.includes(preferred.replace(' premium', ''));
      });
      if (voice && voice.lang.startsWith('en')) {
        this.selectedVoice = voice;
        logger.debug(`Selected voice: ${voice.name}`);
        return;
      }
    }

    // Fallback: find any English voice that's not obviously robotic
    const englishVoices = voices.filter(v => 
      v.lang.startsWith('en') && 
      !v.name.toLowerCase().includes('robotic') &&
      !v.name.toLowerCase().includes('system') &&
      !v.name.toLowerCase().includes('novelty') &&
      !v.name.toLowerCase().includes('pipe organ')
    );

    if (englishVoices.length > 0) {
      // Score all voices and select the highest quality one
      const scoredVoices = englishVoices.map(v => ({
        voice: v,
        score: this.scoreVoiceQuality(v)
      })).sort((a, b) => b.score - a.score);
      
      this.selectedVoice = scoredVoices[0].voice;
      logger.debug(`Selected best quality voice: ${this.selectedVoice.name} (score: ${scoredVoices[0].score})`);
    }
  }

  // Speak text with options
  speak(text, options = {}) {
    if (!this.isSupported) {
      logger.warn('Text-to-speech not supported in this browser');
      return;
    }

    // Stop any current speech
    this.stop();

    // Enhanced default options for more natural, human-like speech
    // Use user preferences if available, otherwise use defaults
    const defaultOptions = {
      rate: this.userPreferences?.rate ?? 0.95,       // Closer to natural human speech rate
      pitch: this.userPreferences?.pitch ?? 1.0,       // Natural pitch
      volume: this.userPreferences?.volume ?? 0.9,    // Clear volume
      lang: 'en-US'     // English
    };

    // Merge with provided options (provided options take precedence)
    const speechOptions = { ...defaultOptions, ...options };

    // Try to use SSML if supported, otherwise fall back to enhanced text
    let speechText = text;
    try {
      // Check if the browser supports SSML (some do, some don't)
      if (this.selectedVoice && this.selectedVoice.localService) {
        // For local voices, try SSML
        speechText = this.createSSML(text, speechOptions);
      } else {
        // For online voices, use enhanced text without SSML tags
        speechText = this.enhanceTextForSpeech(text, options.contentType || 'general')
          .replace(/<[^>]*>/g, '') // Remove SSML tags for compatibility
          .replace(/\s+/g, ' ')
          .trim();
      }
    } catch (error) {
      logger.warn('SSML not supported, using enhanced text:', error);
      speechText = this.enhanceTextForSpeech(text, options.contentType || 'general')
        .replace(/<[^>]*>/g, '') // Remove SSML tags
        .replace(/\s+/g, ' ')
        .trim();
    }

    // Use natural sentence chunking with slight variations for more human delivery
    // Make sure Web Speech is not suspended (Safari/iOS) and WebAudio is unlocked
    try { if (this.synth && this.synth.resume) this.synth.resume(); } catch (_) {}
    this.unlockAudioContext();

    // Ensure we pass plain text to the browser TTS engine (SSML strings are not supported here)
    const plainText = typeof speechText === 'string' 
      ? speechText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() 
      : String(speechText || '');

    // Wait briefly for voices to be ready, then speak
    this.ensureVoicesReady(600).finally(() => {
      this.speakWithNaturalChunks(plainText, speechOptions);
    });
  }

  // Speak with sentence-level chunking and subtle prosody variations
  speakWithNaturalChunks(fullText, baseOptions) {
    const chunks = this.chunkTextIntoSentences(fullText);
    if (chunks.length === 0) return;

    let index = 0;
    const speakNext = () => {
      if (index >= chunks.length) {
        this.isPlaying = false;
        if (typeof baseOptions.onEnd === 'function') baseOptions.onEnd();
        return;
      }

      const raw = chunks[index];
      const trimmed = raw.trim();
      if (!trimmed) {
        index += 1;
        speakNext();
        return;
      }

      // Slight, human-like variations
      const isQuestion = /\?$/.test(trimmed);
      const isExclaim = /!$/.test(trimmed);
      const rateJitter = (Math.random() * 0.06) - 0.03; // ±0.03
      const pitchJitter = (Math.random() * 0.06) - 0.03; // ±0.03

      const utterance = new SpeechSynthesisUtterance(trimmed);
      utterance.rate = Math.max(0.8, Math.min(1.2, (baseOptions.rate ?? 0.95) + rateJitter + (isExclaim ? 0.02 : 0)));
      utterance.pitch = Math.max(0.8, Math.min(1.4, (baseOptions.pitch ?? 1.0) + pitchJitter + (isQuestion ? 0.04 : 0)));
      utterance.volume = baseOptions.volume ?? 0.9;
      utterance.lang = baseOptions.lang ?? 'en-US';

      if (this.selectedVoice) utterance.voice = this.selectedVoice;

      utterance.onstart = () => {
        this.isPlaying = true;
        if (index === 0 && typeof baseOptions.onStart === 'function') baseOptions.onStart();
      };

      utterance.onend = () => {
        // Natural pause between sentences
        index += 1;
        const pauseMs = isQuestion ? 140 : isExclaim ? 120 : 110;
        setTimeout(speakNext, pauseMs);
      };

      utterance.onerror = (event) => {
        logger.error('Speech synthesis error:', event.error);
        this.isPlaying = false;
        if (typeof baseOptions.onError === 'function') baseOptions.onError(event);
      };

      this.currentUtterance = utterance;
      this.synth.speak(utterance);

      // Safety retry: some engines drop the first enqueue after resume/cancel
      setTimeout(() => {
        try {
          if (!this.synth.speaking && this.currentUtterance === utterance) {
            // Retry once without forcing a specific voice
            if (utterance.voice) utterance.voice = null;
            this.synth.speak(utterance);
          }
        } catch (_) {}
      }, 60);
    };

    speakNext();
  }

  // Basic sentence chunking respecting punctuation
  chunkTextIntoSentences(text) {
    try {
      const normalized = String(text).replace(/\s+/g, ' ').trim();
      if (!normalized) return [];
      const parts = normalized.split(/([.!?]+)\s+/).reduce((acc, cur, i, arr) => {
        if (!cur) return acc;
        if (/^[.!?]+$/.test(cur)) {
          // append punctuation to previous
          if (acc.length > 0) acc[acc.length - 1] += cur;
        } else {
          acc.push(cur);
        }
        return acc;
      }, []);
      return parts.length ? parts : [normalized];
    } catch (_) {
      return [String(text)];
    }
  }

  // WebAudio unlock helper (plays a near-silent blip to satisfy autoplay policies)
  // Note: This should only be called after user interaction to avoid browser warnings
  unlockAudioContext() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      
      // Create AudioContext if it doesn't exist (should only be called after user interaction)
      if (!this.audioCtx) {
        // Use options to minimize warnings
        this.audioCtx = new Ctx({
          latencyHint: 'interactive'
        });
      }
      
      // Resume if suspended (gracefully handle errors)
      if (this.audioCtx.state === 'suspended') {
        try {
          this.audioCtx.resume().catch(() => {
            // Silently handle resume errors - expected before user interaction
          });
        } catch (e) {
          // Context may require additional user interaction - silently fail
          return;
        }
      }
      
      // Only proceed if context is running
      if (this.audioCtx.state !== 'running') {
        return;
      }
      
      const osc = this.audioCtx.createOscillator();
      const gain = this.audioCtx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain).connect(this.audioCtx.destination);
      osc.start(this.audioCtx.currentTime);
      osc.stop(this.audioCtx.currentTime + 0.02);
    } catch (_) {
      // Silently handle any errors - this is just an unlock helper
    }
  }

  // Wait until voices are available (or timeout)
  ensureVoicesReady(timeoutMs = 500) {
    return new Promise((resolve) => {
      try {
        const start = Date.now();
        const check = () => {
          const voices = this.getVoices();
          if (voices && voices.length > 0) return resolve();
          if (Date.now() - start > timeoutMs) return resolve();
          setTimeout(check, 50);
        };
        check();
      } catch (_) {
        resolve();
      }
    });
  }

  // Stop current speech
  stop() {
    if (this.synth.speaking) {
      this.synth.cancel();
    }
    this.isPlaying = false;
    this.currentUtterance = null;
  }

  // Toggle speaking: stop if currently speaking, otherwise speak
  toggleSpeak(text, options = {}) {
    if (this.getIsPlaying()) {
      this.stop();
      return 'stopped';
    }
    this.speak(text, options);
    return 'started';
  }

  // Speak question text with natural pacing
  speakQuestion(questionText) {
    // Add natural pauses and emphasis
    const enhancedText = this.enhanceTextForSpeech(questionText);
    this.speak(enhancedText, {
      rate: 0.92,       // Natural conversational pace (slightly slower for clarity)
      pitch: 1.02,      // Slight upward inflection for questions (natural)
      volume: 0.9,      // Clear volume
      onStart: () => logger.debug('Speaking question...'),
      onEnd: () => logger.debug('Question finished')
    });
  }

  // Toggle question speech (same button to start/stop)
  toggleQuestion(questionText) {
    if (this.getIsPlaying()) {
      this.stop();
      return 'stopped';
    }
    this.speakQuestion(questionText);
    return 'started';
  }

  // Speak answer option
  speakAnswer(answerText) {
    this.speak(answerText, {
      rate: 0.95,       // Natural pace, confident but not rushed
      pitch: 1.0,       // Neutral pitch (natural)
      volume: 0.9,      // Clear volume
      onStart: () => logger.debug('Speaking answer...'),
      onEnd: () => logger.debug('Answer finished')
    });
  }

  // Speak word with clear pronunciation (pronounces the word, doesn't spell it)
  speakWord(word) {
    // Use native speech synthesis directly to pronounce the word normally
    // Don't use enhanceWordForSpelling as that adds spaces between letters (spelling mode)
    if ('speechSynthesis' in window) {
      // Stop any current speech
      this.stop();
      
      const cleanWord = String(word || '').trim();
      
      // Use native SpeechSynthesis to pronounce the word, not spell it
      const utterance = new SpeechSynthesisUtterance();
      utterance.text = cleanWord;
      utterance.rate = 0.9;        // Natural pace for word pronunciation
      utterance.pitch = 1.0;       // Natural pitch
      utterance.volume = 0.95;      // Clear volume
      utterance.lang = 'en-US';
      
      // Get the best available voice
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const preferredVoice = voices.find(v => 
          v.lang.startsWith('en') && 
          (v.name.includes('Neural') || v.name.includes('Enhanced') || v.name.includes('Premium'))
        ) || voices.find(v => v.lang.startsWith('en'));
        
        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }
      }
      
      // Resume speech synthesis (important for Safari/iOS)
      try {
        if (window.speechSynthesis.resume) {
          window.speechSynthesis.resume();
        }
      } catch (e) {
        // Ignore resume errors
      }
      
      utterance.onstart = () => {
        this.isPlaying = true;
        logger.debug('Speaking word...');
      };
      
      utterance.onend = () => {
        this.isPlaying = false;
        logger.debug('Word finished');
      };
      
      utterance.onerror = () => {
        this.isPlaying = false;
      };
      
      this.currentUtterance = utterance;
      window.speechSynthesis.speak(utterance);
    } else {
      // Fallback: use speak with general contentType (not phonics, which would spell it)
      this.speak(cleanWord, {
        rate: 0.9,
        pitch: 1.0,
        volume: 0.95,
        contentType: 'general', // Use 'general' not 'phonics' to avoid spelling
        onStart: () => logger.debug('Speaking word...'),
        onEnd: () => logger.debug('Word finished')
      });
    }
  }

  // Toggle word speech (same button to start/stop)
  toggleWord(word) {
    if (this.getIsPlaying()) {
      this.stop();
      return 'stopped';
    }
    this.speakWord(word);
    return 'started';
  }

  // Enhance text for more natural speech using prosody techniques
  enhanceTextForSpeech(text, contentType = 'general') {
    // Clean text first
    let enhanced = text
      .trim()
      // Preserve natural punctuation spacing (browser handles this, but we ensure single spaces)
      .replace(/\s+/g, ' ');

    // Apply educational content enhancements
    enhanced = this.enhanceEducationalContent(enhanced, contentType);

    // Add natural prosody variations to avoid monotone speech
    // This helps the voice sound more conversational and less robotic
    
    // Add slight emphasis to question words (makes questions sound more natural)
    enhanced = enhanced.replace(/\b(what|where|when|why|how|which|who)\b/gi, (match) => {
      // Capitalize properly
      return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
    });

    // Add natural pauses for lists (better rhythm)
    enhanced = enhanced.replace(/(\d+)[\.\)]\s*/g, '$1. ');

    // Improve number pronunciation - convert numbers to words for better clarity
    // Only convert standalone numbers (not part of larger words or contexts where digits are preferred)
    enhanced = enhanced.replace(/\b(\d{1,3})\b/g, (match) => {
      const num = parseInt(match, 10);
      // For small numbers (0-999), convert to words for clarity
      // For larger numbers, keep as digits to avoid overly long speech
      if (num < 1000) {
        return this.numberToWords(num);
      }
      return match; // Keep large numbers as digits
    });

    // Ensure proper spacing around punctuation (browser TTS handles this better with spacing)
    enhanced = enhanced
      .replace(/([.!?])\s*([A-Z])/g, '$1 $2')  // Space after sentence endings
      .replace(/([,:;])\s*/g, '$1 ')            // Consistent comma/colon spacing
      .replace(/\s+/g, ' ')                      // Clean up multiple spaces
      .trim();

    // Remove SSML tags if any were added (for browsers that don't support them)
    enhanced = enhanced.replace(/<[^>]*>/g, '');
    
    return enhanced;
  }

  // Create SSML markup for better speech synthesis
  createSSML(text, options = {}) {
    const {
      rate = 0.95,
      pitch = 1.0,
      volume = 0.9,
      emphasis = 'moderate'
    } = options;

    // Enhanced SSML structure with prosody for more natural speech
    // Add slight prosody variations to avoid monotone delivery
    const enhancedText = this.enhanceTextForSpeech(text);
    
    const ssml = `
      <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="en-US">
        <prosody rate="${rate}" pitch="${pitch}" volume="${volume}">
          ${enhancedText}
        </prosody>
      </speak>
    `.trim();

    return ssml;
  }

  // Enhance word for spelling practice
  enhanceWordForSpelling(word) {
    // Add slight pauses between letters for spelling practice
    return word.split('').join(' ').replace(/\s+/g, ' ');
  }

  // Check if speech is currently playing
  getIsPlaying() {
    return this.isPlaying;
  }

  // Get available voices
  getVoices() {
    return this.synth.getVoices();
  }

  // Get available English voices
  getEnglishVoices() {
    const voices = this.getVoices();
    return voices.filter(v => v.lang.startsWith('en'));
  }

  // Set a specific voice (if available)
  setVoice(voiceName) {
    const voices = this.getVoices();
    const voice = voices.find(v => 
      v.name.includes(voiceName) || 
      v.name.toLowerCase().includes(voiceName.toLowerCase())
    );
    if (voice) {
      this.selectedVoice = voice;
      logger.debug(`Voice changed to: ${voice.name}`);
      return true;
    }
    return false;
  }

  // Get current voice info
  getCurrentVoice() {
    return this.selectedVoice ? {
      name: this.selectedVoice.name,
      lang: this.selectedVoice.lang,
      localService: this.selectedVoice.localService
    } : null;
  }

  // Test a voice with sample text
  testVoice(voiceName, sampleText = "Hello! This is a test of the text-to-speech voice.") {
    const voices = this.getVoices();
    let voice = null;
    
    if (voiceName) {
      voice = voices.find(v => 
        v.name.includes(voiceName) || 
        v.name.toLowerCase().includes(voiceName.toLowerCase())
      );
    }
    
    // Fall back to selected voice if no match
    if (!voice && this.selectedVoice) {
      voice = this.selectedVoice;
    }
    
    // Use user preferences for rate, pitch, volume
    const rate = this.userPreferences?.rate ?? 0.95;
    const pitch = this.userPreferences?.pitch ?? 1.0;
    const volume = this.userPreferences?.volume ?? 0.9;
    
    const utterance = new SpeechSynthesisUtterance(sampleText);
    if (voice) utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;
    utterance.lang = 'en-US';
    
    this.synth.speak(utterance);
    return true;
  }

  // Get voice preferences for settings
  getVoicePreferences() {
    return {
      currentVoice: this.getCurrentVoice(),
      availableVoices: this.getEnglishVoices().map(v => ({
        name: v.name,
        lang: v.lang,
        localService: v.localService,
        quality: this.scoreVoiceQuality(v)
      })),
      preferredVoices: this.preferredVoices,
      userSettings: this.userPreferences
    };
  }

  // Load user preferences from storage
  loadUserPreferences() {
    try {
      const saved = storage.get(STORAGE_KEYS.VOICE_SETTINGS);
      if (saved) {
        return {
          rate: saved.rate ?? 0.95,
          pitch: saved.pitch ?? 1.0,
          volume: saved.volume ?? 0.9,
          voiceName: saved.voiceName ?? null
        };
      }
    } catch (error) {
      logger.warn('Failed to load voice preferences:', error);
    }
    return {
      rate: 0.95,
      pitch: 1.0,
      volume: 0.9,
      voiceName: null
    };
  }

  // Save user preferences to storage
  saveUserPreferences(preferences) {
    try {
      this.userPreferences = {
        ...this.userPreferences,
        ...preferences
      };
      storage.set(STORAGE_KEYS.VOICE_SETTINGS, this.userPreferences);
      
      // If voice name changed, update selected voice
      if (preferences.voiceName) {
        this.setVoice(preferences.voiceName);
      }
      
      logger.debug('Voice preferences saved:', this.userPreferences);
      return true;
    } catch (error) {
      logger.error('Failed to save voice preferences:', error);
      return false;
    }
  }

  // Score voice quality (0-100, higher is better)
  scoreVoiceQuality(voice) {
    let score = 50; // Base score
    
    const nameLower = voice.name.toLowerCase();
    
    // Neural voices get highest score
    if (nameLower.includes('neural') || nameLower.includes('natural')) {
      score += 30;
    }
    
    // Premium/Enhanced voices get bonus
    if (nameLower.includes('premium') || nameLower.includes('enhanced')) {
      score += 20;
    }
    
    // Local voices often sound better
    if (voice.localService) {
      score += 10;
    }
    
    // Preferred voices get bonus
    if (this.preferredVoices.some(pref => 
      nameLower.includes(pref.toLowerCase().replace(/\s*(neural|premium|enhanced)\s*/gi, ''))
    )) {
      score += 15;
    }
    
    // Penalize robotic/novelty voices
    if (nameLower.includes('robotic') || nameLower.includes('novelty') || 
        nameLower.includes('system') || nameLower.includes('pipe organ')) {
      score -= 50;
    }
    
    return Math.max(0, Math.min(100, score));
  }

  // Convert number to words for better pronunciation
  numberToWords(num) {
    if (num === 0) return 'zero';
    if (num < 0) return 'negative ' + this.numberToWords(-num);
    
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
                  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
                  'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    
    if (num < 20) return ones[num];
    if (num < 100) {
      const ten = Math.floor(num / 10);
      const one = num % 10;
      return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
    }
    if (num < 1000) {
      const hundred = Math.floor(num / 100);
      const remainder = num % 100;
      return ones[hundred] + ' hundred' + (remainder > 0 ? ' ' + this.numberToWords(remainder) : '');
    }
    if (num < 1000000) {
      const thousand = Math.floor(num / 1000);
      const remainder = num % 1000;
      return this.numberToWords(thousand) + ' thousand' + (remainder > 0 ? ' ' + this.numberToWords(remainder) : '');
    }
    if (num < 1000000000) {
      const million = Math.floor(num / 1000000);
      const remainder = num % 1000000;
      return this.numberToWords(million) + ' million' + (remainder > 0 ? ' ' + this.numberToWords(remainder) : '');
    }
    
    // For very large numbers, fall back to digit-by-digit for clarity
    return num.toString().split('').join(' ');
  }

  // Enhanced text processing for educational content
  enhanceEducationalContent(text, contentType = 'general') {
    let enhanced = text.trim();
    
    // Apply pronunciation dictionary
    for (const [term, pronunciation] of Object.entries(this.pronunciationDictionary)) {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      enhanced = enhanced.replace(regex, pronunciation);
    }
    
    // Math-specific enhancements
    if (contentType === 'math') {
      // Convert operators to words
      enhanced = enhanced
        .replace(/\s*\+\s*/g, ' plus ')
        .replace(/\s*-\s*/g, ' minus ')
        .replace(/\s*\*\s*/g, ' times ')
        .replace(/\s*×\s*/g, ' times ')
        .replace(/\s*\/\s*/g, ' divided by ')
        .replace(/\s*÷\s*/g, ' divided by ')
        .replace(/\s*=\s*/g, ' equals ');
    }
    
    // Phonics-specific: ensure proper spacing for sound pronunciation
    if (contentType === 'phonics') {
      // Add slight pauses for letter combinations
      enhanced = enhanced.replace(/([a-z]{2,})/gi, (match) => {
        // Don't modify common words
        if (match.length <= 3) return match;
        return match.split('').join(' '); // Space out longer phonics patterns
      });
    }
    
    return enhanced;
  }
}

// Create singleton instance
const textToSpeech = new TextToSpeech();

export default textToSpeech;
